import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiCheck, FiX, FiSave, FiUser, FiList } from 'react-icons/fi';
import api from '../../api';

const RoleFonctionSelector = () => {
  const [roles, setRoles] = useState([]);
  const [allFunctions, setAllFunctions] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedFunctions, setSelectedFunctions] = useState([]);
  const [message, setMessage] = useState('');

  const colors = {
    primary: '#875A7B',
    textLight: '#666666',
    border: '#D9C7D5',
    success: '#2E7D32',
    error: '#C62828',
    light: '#F8F5F7',
    background: '#FFFFFF'
  };

  // 1) Charger les rôles
  useEffect(() => {
    api.get('/parametrages/roles')
      .then(res => setRoles(res.data.roles || []))
      .catch(err => console.error('Erreur GET /roles :', err));
  }, []);

  // 2) Charger les fonctions
  useEffect(() => {
    api.get('/parametrages/functions')
      .then(res => setAllFunctions(res.data.functions || []))
      .catch(err => console.error('Erreur GET /functions :', err));
  }, []);

  // Quand on clique sur un rôle → préremplir selectedFunctions
  const handleRoleClick = (role) => {
    setSelectedRole(role);
    setMessage('');
    const funcIds = Array.isArray(role.functions)
      ? role.functions.map(f => f.id)
      : [];
    setSelectedFunctions(funcIds);
  };

  // Toggle fonction
  const toggleFunction = (funcId) => {
    setSelectedFunctions(prev =>
      prev.includes(funcId)
        ? prev.filter(id => id !== funcId)
        : [...prev, funcId]
    );
  };

  // Sauvegarder la sélection
  const handleSave = () => {
    if (!selectedRole) return;
    api.post(
      `/parametrages/roles/${selectedRole.id}/functions`,
      { function_ids: selectedFunctions }
    )
    .then(() => {
      setMessage('Modifications enregistrées avec succès');
      // Mettre à jour localement le rôle
      setRoles(prev =>
        prev.map(r =>
          r.id === selectedRole.id
            ? { ...r, functions: allFunctions.filter(f => selectedFunctions.includes(f.id)) }
            : r
        )
      );
      setTimeout(() => setMessage(''), 3000);
    })
    .catch(err => {
      console.error('Erreur post /functions :', err);
      setMessage('Erreur lors de la sauvegarde');
    });
  };

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: colors.light }}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center mb-8">
          <div className="p-3 rounded-lg mr-4" style={{ backgroundColor: colors.primary, color: 'white' }}>
            <FiUser size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: colors.primary }}>
              Gestion des Autorisations
            </h1>
            <p className="text-sm" style={{ color: colors.textLight }}>
              Attribution des fonctions aux rôles
            </p>
          </div>
        </div>

        {/* Sélecteur de rôle */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8" style={{ border: `1px solid ${colors.border}` }}>
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <FiList className="mr-2" style={{ color: colors.primary }} />
            <span style={{ color: colors.primary }}>Sélectionnez un rôle</span>
          </h2>
          <div className="flex flex-wrap gap-3">
            {roles.map(role => (
              <button
                key={role.id}
                onClick={() => handleRoleClick(role)}
                className={`px-4 py-2 rounded-lg transition-all duration-200 flex items-center ${
                  selectedRole?.id === role.id ? 'text-white shadow-md' : 'hover:bg-gray-50'
                }`}
                style={{
                  backgroundColor:
                    selectedRole?.id === role.id ? colors.primary : colors.background,
                  border: `1px solid ${
                    selectedRole?.id === role.id ? colors.primary : colors.border
                  }`
                }}
              >
                {role.nom}
              </button>
            ))}
          </div>
        </div>

        {/* Grille de fonctions */}
        {selectedRole && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-8" style={{ border: `1px solid ${colors.border}` }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: colors.primary }}>
              Fonctions disponibles pour : <span className="font-bold">{selectedRole.nom}</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {allFunctions.map(func => {
                const isSel = selectedFunctions.includes(func.id);
                return (
                  <div
                    key={func.id}
                    onClick={() => toggleFunction(func.id)}
                    className={`p-3 rounded-lg cursor-pointer flex items-center transition-all duration-200 ${
                      isSel ? 'border-l-4' : 'border-l-4 border-transparent'
                    }`}
                    style={{
                      backgroundColor: isSel ? `${colors.primary}10` : colors.background,
                      borderLeftColor: isSel ? colors.primary : 'transparent',
                      border: `1px solid ${colors.border}`
                    }}
                  >
                    <div className={`w-5 h-5 rounded flex items-center justify-center mr-3 ${
                      isSel ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                      {isSel ? <FiCheck size={14} /> : <FiX size={14} />}
                    </div>
                    <span style={{ color: colors.textLight || '#333' }}>{func.nom}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Bouton Enregistrer & message */}
        {selectedRole && (
          <div className="flex justify-between items-center">
            <div className="text-sm" style={{ color: message.includes('Erreur') ? colors.error : colors.success }}>
              {message && (
                <div className={`px-4 py-2 rounded-lg inline-flex items-center ${
                  message.includes('Erreur') ? 'bg-red-50' : 'bg-green-50'
                }`}>
                  {message}
                </div>
              )}
            </div>
            <button
              onClick={handleSave}
              className="px-6 py-3 rounded-lg font-medium flex items-center transition-all duration-200 hover:shadow-md text-white"
              style={{ backgroundColor: colors.primary }}
            >
              <FiSave className="mr-2" /> Enregistrer
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleFonctionSelector;
